import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Building2, Eye, ThumbsUp, ExternalLink, Tag, Clock, TrendingUp, Sparkles, Timer, Megaphone, BarChart3 } from 'lucide-react';
import { getEventById } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDuration } from '../utils/format';
import { format, parseISO } from 'date-fns';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long this ad ran. A live ad has no end date — its span is a lower bound
 * that keeps growing, so it is marked as such rather than presented as final.
 */
function adRunLength(rawAd) {
  const start = rawAd.start_date ? new Date(rawAd.start_date) : null;
  if (!start || Number.isNaN(start.getTime())) return { label: '—', isOngoing: false };

  if (rawAd.is_active) {
    const days = Math.max(1, Math.round((Date.now() - start.getTime()) / DAY_MS));
    return { label: formatDuration(days, { ongoing: true }), isOngoing: true };
  }

  const end = rawAd.end_date ? new Date(rawAd.end_date) : null;
  if (!end || Number.isNaN(end.getTime()) || end < start) return { label: '—', isOngoing: false };
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS));
  return { label: formatDuration(days), isOngoing: false };
}

function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = event ? `${event.event_title || 'Event'} | Dental Events` : 'Event | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, [id, event?.event_title]);

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await getEventById(id);
      setEvent(response.data.data.event);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load event details'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dental-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Event not found'}</p>
        <Link to="/events" className="btn-primary">
          Back to Events
        </Link>
      </div>
    );
  }

  const rawAd = event.RawAd || {};
  const adDuration = adRunLength(rawAd);
  // Facebook only ever gives a coarse impressions band, and often not even that.
  const adReach = { label: rawAd.impressions?.impressions_text || '—' };

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <Link to="/events" className="inline-flex items-center gap-2 text-dental-600 hover:text-dental-700">
        <ArrowLeft className="w-4 h-4" />
        Back to Events
      </Link>

      {/* Header */}
      <div className="card">
        <div className="flex flex-col gap-4">
          {/* Event Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <h1 className="text-2xl font-bold break-words min-w-0">
                {event.event_title || 'Dental Event'}
              </h1>
              {rawAd.ad_url && (
                <a
                  href={rawAd.ad_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap shrink-0 w-full sm:w-auto"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Real Ad
                </a>
              )}
            </div>

            {event.offer && (
              <span className="badge badge-success text-base px-4 py-1 mt-3 inline-block">
                {event.offer}
              </span>
            )}

            <div className="space-y-3 mt-4">
              {event.clinic_name && (
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Clinic</p>
                    <p className="font-medium">{event.clinic_name}</p>
                  </div>
                </div>
              )}

              {event.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{event.location}</p>
                    {event.location_normalized && event.location_normalized !== event.location && (
                      <p className="text-xs text-gray-400">Normalized: {event.location_normalized}</p>
                    )}
                  </div>
                </div>
              )}

              {(event.event_start_date || event.event_date) && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Event Date</p>
                    <p className="font-medium">
                      {event.event_start_date 
                        ? format(parseISO(event.event_start_date), 'EEEE, MMMM d, yyyy')
                        : event.event_date}
                      {event.event_end_date && event.event_end_date !== event.event_start_date && (
                        <span> - {format(parseISO(event.event_end_date), 'MMMM d, yyyy')}</span>
                      )}
                    </p>
                    {event.is_recurring && (
                      <p className="text-sm text-dental-600">
                        🔄 Recurring: {event.recurring_pattern || 'Yes'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {event.contact_info && (
                <div className="flex items-center gap-3">
                  <ExternalLink className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">{event.contact_info}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Impressions and run length. The old "Min/Max Reach (EU)" tiles read
            eu_total_reach_min/max, which this API never returns — they showed
            "—" for every ad, always. */}
        <div className="card text-center">
          <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{adReach.label}</p>
          <p className="text-sm text-gray-500">Impressions</p>
        </div>
        <div className="card text-center">
          <Timer className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{adDuration.label}</p>
          <p className="text-sm text-gray-500">
            {adDuration.isOngoing ? 'Running so far' : 'Ran for'}
          </p>
        </div>
        <div className="card text-center">
          <ThumbsUp className="w-6 h-6 text-pink-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">
            {rawAd.page_like_count?.toLocaleString() || '—'}
          </p>
          <p className="text-sm text-gray-500">Page Likes</p>
        </div>
        <div className="card text-center">
          <Sparkles className="w-6 h-6 text-dental-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">
            {event.confidence_score ? `${Math.round(event.confidence_score * 100)}%` : '—'}
          </p>
          <p className="text-sm text-gray-500">AI Confidence</p>
        </div>
      </div>

      {/* Every ad promoting this event, not just the one this row came from. */}
      {event.ads?.length > 0 && (
        <div className="card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Ads for this event
              <span className="text-sm font-normal text-gray-500">
                {event.adCount} total · {event.activeAdCount} still running
              </span>
            </h2>
            <span className="flex items-center gap-2">
              {event.lifecycleLabel && (
                <span className="badge bg-gray-100 text-gray-600 text-xs">{event.lifecycleLabel}</span>
              )}
              <Link
                to={`/ad-stats?clinic=${encodeURIComponent(event.clinic_name || '')}`}
                className="btn-secondary text-xs py-1 px-2 inline-flex items-center gap-1.5"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Ad statistics
              </Link>
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 text-xs">
                  <th className="px-2 py-2 text-left font-medium">Page</th>
                  <th className="px-2 py-2 text-left font-medium">Started</th>
                  <th className="px-2 py-2 text-left font-medium">Ran for</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                  <th className="px-2 py-2 text-right font-medium">Reach</th>
                  <th className="px-2 py-2 text-right font-medium">Link</th>
                </tr>
              </thead>
              <tbody>
                {event.ads.map((ad) => (
                  <tr key={ad.adArchiveId} className="border-b border-gray-100 last:border-0">
                    <td className="px-2 py-2 text-gray-700">{ad.pageName || '—'}</td>
                    <td className="px-2 py-2 text-gray-600 whitespace-nowrap">
                      {ad.startDate ? format(parseISO(ad.startDate), 'd MMM yyyy') : '—'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {ad.duration?.label || '—'}
                      {/* A short run means nothing if another ad took over. */}
                      {ad.wasReplaced && (
                        <span className="badge bg-gray-100 text-gray-500 text-[10px] ml-1.5">replaced</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`badge text-[10px] ${ad.isActive ? 'badge-success' : 'bg-gray-100 text-gray-500'}`}>
                        {ad.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                      {ad.reach?.label || '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {ad.adUrl ? (
                        <a href={ad.adUrl} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {event.audience ? (
            <div className="mt-4 pt-3 border-t border-gray-100 grid sm:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-gray-500 mb-0.5">People reached (largest ad)</p>
                <p className="text-base font-semibold tabular-nums">
                  {event.audience.maxAdReach?.toLocaleString() ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 mb-0.5">Targeted</p>
                <p>
                  {event.audience.ageMin ?? '?'}–{event.audience.ageMax ?? '?'}
                  {event.audience.gender ? ` · ${event.audience.gender}` : ''}
                </p>
              </div>
              <div>
                <p className="text-gray-500 mb-0.5">Cities</p>
                <p>{event.audience.locationLabels?.join(' · ') || '—'}</p>
              </div>
              {event.estimatedSpend && (
                <div className="sm:col-span-3 pt-2 border-t border-gray-100">
                  <span className="text-gray-500">Estimated spend </span>
                  <strong className="text-gray-900">
                    £{event.estimatedSpend.min.toLocaleString()}–£{event.estimatedSpend.max.toLocaleString()}
                  </strong>
                  <span className="text-gray-400">
                    {' '}— from the largest ad&apos;s reach at {event.estimatedSpend.cpm.minCpm}–
                    {event.estimatedSpend.cpm.maxCpm} CPM. An estimate: the Ad Library reports no spend.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
              No audience data yet — press Update on the Ad Statistics page to pull targeting,
              age split and exact reach for these {event.adCount} ad{event.adCount === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      )}

      {/* Treatment Types */}
      {event.treatment_types && event.treatment_types.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Treatment Types
          </h2>
          <div className="flex flex-wrap gap-2">
            {event.treatment_types.map((type) => (
              <span key={type} className="badge badge-primary text-sm px-3 py-1">
                {type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ad Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ad Copy */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">Ad Copy</h2>
          <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
            {rawAd.ad_creative_bodies?.[0] || event.raw_ad_text || 'No ad copy available'}
          </div>
        </div>

        {/* Link Information */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">Ad Information</h2>
          <div className="space-y-3">
            {rawAd.page_name && (
              <div>
                <p className="text-sm text-gray-500">Advertiser Page</p>
                <p className="font-medium">{rawAd.page_name}</p>
              </div>
            )}
            {rawAd.link_url && (
              <div>
                <p className="text-sm text-gray-500">Landing Page</p>
                <a
                  href={rawAd.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dental-600 hover:text-dental-700 text-sm break-all"
                >
                  {rawAd.link_url}
                </a>
              </div>
            )}
            <div className="pt-3 border-t">
              {rawAd.ad_url ? (
                <a
                  href={rawAd.ad_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Ad on Facebook Ad Library
                </a>
              ) : (
                <p className="text-gray-400 text-sm text-center">Ad URL not available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Page Information */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-3">Advertiser Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Page Name</p>
            <p className="font-medium">{rawAd.page_name || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Page ID</p>
            <p className="font-mono text-sm">{rawAd.page_id || '—'}</p>
          </div>
          {/* These four used to read ad_active_status / ad_delivery_start_time /
              ad_delivery_stop_time / publisher_platforms — none of which exist
              on RawAd, so "Stopped" said "Still running" for every ad forever.
              Bound to the real columns now. */}
          <div>
            <p className="text-sm text-gray-500">Ad Status</p>
            <span className={`badge ${rawAd.is_active ? 'badge-success' : 'badge-secondary'}`}>
              {rawAd.is_active ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500">Started</p>
            <p className="text-sm">
              {rawAd.start_date
                ? format(parseISO(rawAd.start_date), 'MMM d, yyyy')
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Stopped</p>
            <p className="text-sm">
              {rawAd.is_active
                ? 'Still running'
                : rawAd.end_date
                  ? format(parseISO(rawAd.end_date), 'MMM d, yyyy')
                  : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Publisher Platforms</p>
            <p className="text-sm">
              {(Array.isArray(rawAd.publisher_platform)
                ? rawAd.publisher_platform.join(', ')
                : rawAd.publisher_platform) || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      {event.ai_reasoning && (
        <div className="card border-dental-200 bg-dental-50">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-dental-700">
              <Sparkles className="w-5 h-5" />
              AI Analysis
            </h2>
            {event.ai_reasoning?.image_analysis_used && (
              <span className="badge bg-purple-100 text-purple-700">
                📷 Image Analysis Used
              </span>
            )}
            {event.ai_reasoning?.analysis_source && (
              <span className="text-xs text-dental-600">
                Source: {event.ai_reasoning.analysis_source}
              </span>
            )}
          </div>
          <div className="text-sm text-dental-800 space-y-2">
            {typeof event.ai_reasoning === 'object' ? (
              <>
                {event.ai_reasoning.additional_details && (
                  <p><strong>Details:</strong> {event.ai_reasoning.additional_details}</p>
                )}
                {event.ai_reasoning.parse_error && (
                  <p className="text-red-600"><strong>Parse Error:</strong> {event.ai_reasoning.parse_error}</p>
                )}
              </>
            ) : (
              <p className="whitespace-pre-wrap">{event.ai_reasoning}</p>
            )}
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="text-sm text-gray-400 flex items-center gap-4">
        <span>
          <Clock className="w-4 h-4 inline mr-1" />
          Created: {format(new Date(event.createdAt), 'MMM d, yyyy HH:mm')}
        </span>
        <span>
          Updated: {format(new Date(event.updatedAt), 'MMM d, yyyy HH:mm')}
        </span>
      </div>
    </div>
  );
}

export default EventDetail;
